import { useCallback, useEffect, useRef, useState } from "react";
import {
  cancelMatchmaking,
  createOpaqueKey,
  pollMatchmaking,
  startMatchmaking,
  type DeviceSession,
  type MatchmakingMode,
  type MatchmakingTicket,
} from "../game/api";

export interface MatchmakingState {
  phase: "idle" | "starting" | "searching" | "error";
  ticket: MatchmakingTicket | null;
  selectedMode: MatchmakingMode;
  error: string | null;
}

export interface MatchmakingController extends MatchmakingState {
  start: (mode: MatchmakingMode) => void;
  retry: () => void;
  cancel: () => Promise<void>;
  reset: () => void;
}

export function useMatchmaking(
  session: DeviceSession | null,
  onMatched: (ticket: MatchmakingTicket) => void,
): MatchmakingController {
  const [state, setState] = useState<MatchmakingState>({
    phase: "idle",
    ticket: null,
    selectedMode: "casual",
    error: null,
  });
  const onMatchedRef = useRef(onMatched);
  const startControllerRef = useRef<AbortController | null>(null);
  const attemptRef = useRef<{ mode: MatchmakingMode; key: string } | null>(null);
  onMatchedRef.current = onMatched;

  const begin = useCallback((mode: MatchmakingMode, key: string) => {
    if (!session) {
      setState({ phase: "error", ticket: null, selectedMode: mode, error: "Online session unavailable." });
      return;
    }
    startControllerRef.current?.abort();
    const controller = new AbortController();
    startControllerRef.current = controller;
    attemptRef.current = { mode, key };
    setState({ phase: "starting", ticket: null, selectedMode: mode, error: null });
    void startMatchmaking(session, mode, key, controller.signal)
      .then((ticket) => {
        if (controller.signal.aborted) return;
        if (ticket.status === "matched" && ticket.connection) {
          attemptRef.current = null;
          setState({ phase: "idle", ticket: null, selectedMode: mode, error: null });
          onMatchedRef.current(ticket);
          return;
        }
        setState({ phase: "searching", ticket, selectedMode: mode, error: null });
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        setState({
          phase: "error",
          ticket: null,
          selectedMode: mode,
          error: error instanceof Error ? error.message : "Matchmaking is unavailable.",
        });
      });
  }, [session]);

  const start = useCallback((mode: MatchmakingMode) => {
    begin(mode, createOpaqueKey());
  }, [begin]);

  const retry = useCallback(() => {
    const attempt = attemptRef.current;
    begin(attempt?.mode ?? state.selectedMode, attempt?.key ?? createOpaqueKey());
  }, [begin, state.selectedMode]);

  useEffect(() => {
    if (!session || state.phase !== "searching" || !state.ticket) return;
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      void pollMatchmaking(
        session,
        state.ticket?.ticketId ?? "",
        state.ticket?.searchTicket ?? "",
        state.selectedMode,
        controller.signal,
      )
        .then((ticket) => {
          if (controller.signal.aborted) return;
          if (ticket.status === "matched") {
            if (ticket.connection) {
              attemptRef.current = null;
              setState((current) => ({ ...current, phase: "idle", ticket: null, error: null }));
              onMatchedRef.current(ticket);
            }
            else setState((current) => ({ ...current, phase: "error", error: "The match response was incomplete." }));
            return;
          }
          if (ticket.status === "cancelled" || ticket.status === "expired") {
            attemptRef.current = null;
            setState((current) => ({ ...current, phase: "error", ticket, error: `Search ${ticket.status}.` }));
            return;
          }
          setState((current) => ({ ...current, ticket }));
        })
        .catch((error: unknown) => {
          if (controller.signal.aborted) return;
          setState((current) => ({
            ...current,
            phase: "error",
            error: error instanceof Error ? error.message : "Matchmaking polling stopped.",
          }));
        });
    }, state.ticket.pollAfterMs);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [session, state.phase, state.selectedMode, state.ticket]);

  const cancel = useCallback(async () => {
    startControllerRef.current?.abort();
    const ticket = state.ticket;
    setState((current) => ({ ...current, phase: "idle", ticket: null, error: null }));
    attemptRef.current = null;
    if (!session || !ticket) return;
    try {
      await cancelMatchmaking(session, ticket.ticketId, ticket.searchTicket);
    } catch {
      // Local cancellation is immediate; server expiry remains the fallback.
    }
  }, [session, state.ticket]);

  const reset = useCallback(() => {
    startControllerRef.current?.abort();
    attemptRef.current = null;
    setState({ phase: "idle", ticket: null, selectedMode: "casual", error: null });
  }, []);

  useEffect(() => () => startControllerRef.current?.abort(), []);

  return { ...state, start, retry, cancel, reset };
}
